import AddCircleIcon from '@assets/icons/add-circle.svg';
import AlertIcon from '@assets/icons/alert.svg';
import CashIcon from '@assets/icons/cash.svg';
import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import i18n from '@/utils/i18n';
import KlarnaBadgeIcon from '@assets/icons/klarna-badge.svg';
import PaperCheckIcon from '@assets/icons/paper-check.svg';
import React, { useEffect, useMemo, useState } from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Badge, BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { getPaymentMethodFromPaymentMethodOption } from '@/utils/payments';
import { PaymentMethod, PaymentMethodType } from '@/enums';
import { PaymentMethodIcon } from './payment-method-icon';
import { selectVariant, useKlarnaStore } from '@/stores/use-klarna-store';
import { TextInput } from '@/components/text-input';
import { TextStyle, TouchableOpacity, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCheckCaptureStore } from '@/stores/check-capture-store';
import { useGetPadPaymentMethodConsent } from '@/hooks/payments/use-pad-payment-operations';
import { useLayoutContext } from '@/contexts/layout-context';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useToasts } from '@/contexts/toast-context';

export interface PaymentReviewAction {
  content: React.ReactNode;
  key: string;
  leftIcon?: React.ReactNode;
  onPress: () => void;
}

export interface Props {
  additionalActions?: PaymentReviewAction[];
  calculatedTransactionFee: number;
  checkNumber?: string;
  isCash?: boolean;
  isKlarnaBusy?: boolean;
  isManualCheck?: boolean;
  isSubmitting: boolean;
  onChange: () => void;
  onChangeCheckNumber?: (value: string) => void;
  onCollectPADConsent: () => void;
  onKlarnaContinue?: () => Promise<boolean>;
  onSubmit: () => Promise<void>;
  onViewPADAgreement: () => void;
  payAmountInMicrosOverride?: number | null;
  reviewBanner?: React.ReactNode;
  subtotalInMicros: number;
  transactionFeeName: string | null;
}

export const PaymentReviewComponent: React.FC<Props> = ({
  additionalActions,
  calculatedTransactionFee,
  checkNumber,
  isCash = false,
  isKlarnaBusy,
  isManualCheck = false,
  isSubmitting,
  onChangeCheckNumber,
  onChange,
  onCollectPADConsent,
  onKlarnaContinue,
  onSubmit,
  onViewPADAgreement,
  payAmountInMicrosOverride,
  reviewBanner,
  subtotalInMicros,
  transactionFeeName,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();
  const {
    currencyCode,
    loading,
    memo,
    selectedPaymentMethod,
    setPaymentContextState,
    showChargebackDisclaimer,
  } = usePaymentsContext();

  const checkSelected = useCheckCaptureStore((s) => s.submission.checkSelected);
  const imageValidationError = useCheckCaptureStore(
    (s) => s.submission.imageValidationError,
  );
  const klarnaVariant = useKlarnaStore(selectVariant);

  const getPadPaymentMethodConsent = useGetPadPaymentMethodConsent();
  const [showPADConsentForm, setShowPADConsentForm] = useState(false);
  const [
    isLoadingPadPaymentMethodConsent,
    setIsLoadingPadPaymentMethodConsent,
  ] = useState(false);

  useEffect(() => {
    const fetchPadPaymentMethodConsent = async (paymentMethodUUID: string) => {
      setShowPADConsentForm(false);
      setIsLoadingPadPaymentMethodConsent(true);
      try {
        const { data } = await getPadPaymentMethodConsent({
          variables: {
            paymentMethodUUID,
          },
        });

        const hasConsent = Boolean(data?.pad_payment_method[0]?.consent_date);
        setShowPADConsentForm(!hasConsent);
      } catch {
        showErrorToast();
      } finally {
        setIsLoadingPadPaymentMethodConsent(false);
      }
    };

    if (selectedPaymentMethod?.type === PaymentMethodType.PAD) {
      fetchPadPaymentMethodConsent(selectedPaymentMethod.uuid);
    } else {
      setShowPADConsentForm(false);
    }
  }, [getPadPaymentMethodConsent, selectedPaymentMethod, showErrorToast]);

  const paymentMethod: PaymentMethod = useMemo(() => {
    return getPaymentMethodFromPaymentMethodOption(selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  const total = subtotalInMicros + calculatedTransactionFee;
  const payAmountInMicros =
    typeof payAmountInMicrosOverride === 'number'
      ? payAmountInMicrosOverride
      : total;
  const hasAdditionalActions = Boolean(
    additionalActions && additionalActions.length > 0,
  );
  const [memoEditing, setMemoEditing] = useState(Boolean(memo));

  return (
    <>
      {isSmallLayout ? (
        <View style={styles.title}>
          <Typography variant="heading-lg">{i18n.t('send_payment')}</Typography>
        </View>
      ) : null}
      <View style={styles.totalsBlock}>
        <View style={styles.totalsRow}>
          <Typography variant="paragraph-base">{i18n.t('subtotal')}</Typography>
          <Typography variant="paragraph-base-600">
            {formatCurrency(subtotalInMicros, currencyCode)}
          </Typography>
        </View>
        {calculatedTransactionFee > 0 ? (
          <View style={styles.totalsRow}>
            <Typography variant="paragraph-base">
              {transactionFeeName
                ? transactionFeeName
                : i18n.t('transaction_fee')}
            </Typography>
            <Typography variant="paragraph-base-600">
              {formatCurrency(calculatedTransactionFee, currencyCode)}
            </Typography>
          </View>
        ) : null}
        <View style={styles.totalsRow}>
          <Typography variant="paragraph-base">{i18n.t('total')}</Typography>
          <Typography variant="paragraph-base-600">
            {formatCurrency(total, currencyCode)}
          </Typography>
        </View>
      </View>
      {checkSelected ? (
        <View style={[styles.actionRowTopDivider]}>
          <View style={styles.actionRow}>
            <View style={styles.actionLeft}>
              <ScanIcon
                height={24}
                stroke={theme.sk.color.icon.primary}
                width={24}
              />
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-base"
              >
                {i18n.t('check_capture.row_title')}
              </Typography>
            </View>
            <View style={styles.actionRight}>
              <Button
                onPress={onChange}
                size="xsmall"
                style={styles.changeButton}
                trackingData={{
                  name: 'payments.change_payment_method_button',
                }}
                type="tertiary"
              >
                {i18n.t('change')}
              </Button>
            </View>
          </View>
          {imageValidationError &&
          (imageValidationError.front || imageValidationError.rear) ? (
            <View style={styles.errorRow}>
              <AlertIcon
                height={16}
                stroke={theme.sk.color.icon.negative.primary}
                width={16}
              />
              <Typography
                color={theme.sk.color.text.error}
                variant="paragraph-sm"
              >
                {imageValidationError.message}
              </Typography>
            </View>
          ) : null}
        </View>
      ) : klarnaVariant ? (
        <View style={[styles.actionRow, styles.actionRowTopDivider]}>
          <View style={styles.actionLeft}>
            <KlarnaBadgeIcon height={24} width={57} />
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {i18n.t(
                klarnaVariant === PaymentMethodType.KLARNA
                  ? 'klarna.pay_later'
                  : 'klarna.pay_over_time',
              )}
            </Typography>
          </View>
          <View style={styles.actionRight}>
            <Button
              onPress={onChange}
              size="xsmall"
              style={styles.changeButton}
              trackingData={{
                name: 'payments.change_payment_method_button',
              }}
              type="tertiary"
            >
              {i18n.t('change')}
            </Button>
          </View>
        </View>
      ) : selectedPaymentMethod && !loading ? (
        <View style={[styles.actionRow, styles.actionRowTopDivider]}>
          <View style={styles.actionLeft}>
            <PaymentMethodIcon type={paymentMethod} />
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {selectedPaymentMethod.displayText}
            </Typography>
            {Boolean(
              selectedPaymentMethod.cashbackPercentage &&
                selectedPaymentMethod.cashbackPercentage > 0,
            ) ? (
              <Badge
                alignment="center"
                label={i18n.t('tab_card.cashback_badge', {
                  cashbackPercentage: selectedPaymentMethod.cashbackPercentage,
                })}
                size={BadgeSize.SMALL}
                variant={BadgeVariant.SUCCESS}
              />
            ) : null}
          </View>
          <View style={styles.actionRight}>
            <Button
              onPress={onChange}
              size="xsmall"
              style={styles.changeButton}
              trackingData={{
                name: 'payments.change_payment_method_button',
              }}
              type="tertiary"
            >
              {i18n.t('change')}
            </Button>
          </View>
        </View>
      ) : loading ? (
        <ActivityIndicator style={styles.loadingIndicator} />
      ) : isManualCheck ? (
        <View style={styles.actionRowTopDivider}>
          <View style={styles.actionRow}>
            <View style={styles.actionLeft}>
              <PaperCheckIcon
                height={24}
                stroke={theme.sk.color.icon.primary}
                width={24}
              />
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-base"
              >
                {i18n.t('check')}
              </Typography>
            </View>
            <View style={styles.actionRight}>
              <Button
                onPress={onChange}
                size="xsmall"
                style={styles.changeButton}
                trackingData={{
                  name: 'payments.change_payment_method_button',
                }}
                type="tertiary"
              >
                {i18n.t('change')}
              </Button>
            </View>
          </View>
          <TextInput
            accessibilityLabel={i18n.t('check_number')}
            containerStyle={styles.checkNumberInputContainer}
            focusedBorderColor={theme.sk.color.border.input.active}
            keyboardType="number-pad"
            label={i18n.t('check_number')}
            labelStyle={theme.sk.typography.label.md as TextStyle}
            onChangeText={onChangeCheckNumber}
            placeholder={i18n.t('check_number_placeholder')}
            selectionColor={theme.sk.color.border.input.active}
            showDoneButtonAccessoryView
            useOuterLabel
            value={checkNumber ?? ''}
          />
        </View>
      ) : isCash ? (
        <View style={[styles.actionRow, styles.actionRowTopDivider]}>
          <View style={styles.actionLeft}>
            <CashIcon
              height={24}
              stroke={theme.sk.color.icon.primary}
              width={24}
            />
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {i18n.t('cash')}
            </Typography>
          </View>
          <View style={styles.actionRight}>
            <Button
              onPress={onChange}
              size="xsmall"
              style={styles.changeButton}
              trackingData={{
                name: 'payments.change_payment_method_button',
              }}
              type="tertiary"
            >
              {i18n.t('change')}
            </Button>
          </View>
        </View>
      ) : (
        <ActivityIndicator style={styles.loadingIndicator} />
      )}
      {showChargebackDisclaimer ? (
        <Typography color={theme.sk.color.text.tertiary} variant="paragraph-sm">
          {i18n.t('chargeback_disclaimer')}
        </Typography>
      ) : null}
      {hasAdditionalActions
        ? additionalActions?.map((action) => (
            <TouchableOpacity
              accessibilityRole="button"
              key={action.key}
              onPress={action.onPress}
              style={[styles.actionRow, styles.actionRowTopDivider]}
            >
              {action.leftIcon ? (
                <View style={styles.actionLeftIcon}>{action.leftIcon}</View>
              ) : null}
              <View style={styles.actionContent}>
                {typeof action.content === 'string' ? (
                  <Typography
                    color={theme.sk.color.text.primary}
                    variant="paragraph-base"
                  >
                    {action.content}
                  </Typography>
                ) : (
                  action.content
                )}
              </View>
              <ChevronRightThinIcon
                height={16}
                stroke={theme.sk.color.icon.tertiary}
                width={16}
              />
            </TouchableOpacity>
          ))
        : null}
      {/* TODO: Fix mobile blur event for TextInput when rendered on top of a modal */}
      {memoEditing ? (
        <View style={[styles.actionRow, styles.actionRowTopDivider]}>
          <View style={styles.actionLeftIcon}>
            <AddCircleIcon
              height={24}
              stroke={theme.sk.color.icon.secondary}
              width={24}
            />
          </View>
          <TextInput
            autoFocus
            containerStyle={styles.memoInputContainer}
            onBlur={() => {
              if (!memo) {
                setMemoEditing(false);
              }
            }}
            onChangeText={(value: string) =>
              setPaymentContextState({ memo: value })
            }
            placeholder={i18n.t('memo')}
            showDoneButtonAccessoryView
            value={memo}
          />
        </View>
      ) : (
        <TouchableOpacity
          accessibilityLabel={i18n.t('memo')}
          accessibilityRole="button"
          onPress={() => setMemoEditing(true)}
          style={[styles.actionRow, styles.actionRowTopDivider]}
        >
          <View style={styles.actionLeftIcon}>
            <AddCircleIcon
              height={24}
              stroke={theme.sk.color.icon.secondary}
              width={24}
            />
          </View>
          <View style={styles.actionContent}>
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {memo || i18n.t('memo')}
            </Typography>
          </View>
        </TouchableOpacity>
      )}
      {reviewBanner ? (
        <View style={styles.reviewBannerContainer}>{reviewBanner}</View>
      ) : null}
      {klarnaVariant && onKlarnaContinue ? (
        <Button
          loading={isKlarnaBusy}
          onPress={onKlarnaContinue}
          style={styles.payButton}
          trackingData={{ name: 'payments.klarna_continue_button' }}
        >
          {i18n.t('continue_with_klarna')}
        </Button>
      ) : (
        <Button
          disabled={isManualCheck && !checkNumber?.trim()}
          loading={isSubmitting || isLoadingPadPaymentMethodConsent}
          onPress={showPADConsentForm ? onCollectPADConsent : onSubmit}
          style={styles.payButton}
          testID="payment-submit-button"
          trackingData={{ name: 'payments.submit_button' }}
        >
          {i18n.t('pay_amount', {
            paymentAmount: formatCurrency(payAmountInMicros, currencyCode),
          })}
        </Button>
      )}
      {paymentMethod === PaymentMethod.PAD && !showPADConsentForm ? (
        <View style={styles.padAgreementMessage}>
          <Typography variant="paragraph-sm">
            {i18n.t('pad.view_agreement_message_beginning')}
          </Typography>
          <TouchableOpacity onPress={onViewPADAgreement}>
            <Typography textDecorationLine="underline" variant="paragraph-sm">
              {i18n.t('pad.view_agreement_message_link')}
            </Typography>
          </TouchableOpacity>
        </View>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  actionContent: {
    flex: 1,
    marginRight: theme.sk.spacing.spaceXsmall,
  },
  actionLeft: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  actionLeftIcon: {
    alignItems: 'center',
    height: 24,
    justifyContent: 'center',
    marginRight: theme.sk.spacing.spaceMedium,
    width: 24,
  },
  actionRight: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  actionRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 56,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  actionRowTopDivider: {
    borderTopColor: theme.sk.color.divider.default,
    borderTopWidth: 1,
  },
  changeButton: {
    height: 24,
    paddingHorizontal: 0,
  },
  checkNumberInputContainer: {
    borderColor: theme.sk.color.border.input.default,
    marginLeft: 0,
    marginTop: theme.sk.spacing.spaceSmall,
  },
  errorRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    paddingBottom: theme.sk.spacing.spaceSmall,
  },
  loadingIndicator: {
    paddingBottom: theme.sk.spacing.spaceLarge,
  },
  memoInputContainer: {
    borderColor: theme.sk.color.border.input.default,
    flex: 1,
    marginLeft: 0,
  },
  padAgreementMessage: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: theme.sk.spacing.spaceSmall,
  },
  payButton: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
  reviewBannerContainer: {
    marginVertical: theme.sk.spacing.spaceSmall,
  },
  title: {
    marginBottom: theme.sk.spacing.spaceMedium,
  },
  totalsBlock: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceXsmall,
    marginBottom: theme.sk.spacing.spaceMedium,
  },
  totalsRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));
